import {
  WorkflowCategoryData,
  WorkflowStepData,
  OfficeType,
  SubmitStepPayload,
  SubmitFeedbackPayload,
  DeleteStepPayload,
  FeedbackEntry,
} from "../../types/process";

// Action Types
export enum ProcessActionTypes {
  // Workflow Actions
  FETCH_WORKFLOWS_REQUEST = "@process/FETCH_WORKFLOWS_REQUEST",
  FETCH_WORKFLOWS_SUCCESS = "@process/FETCH_WORKFLOWS_SUCCESS",
  FETCH_WORKFLOWS_FAILURE = "@process/FETCH_WORKFLOWS_FAILURE",

  SUBMIT_STEP_CONFIG_REQUEST = "@process/SUBMIT_STEP_CONFIG_REQUEST",
  SUBMIT_STEP_CONFIG_SUCCESS = "@process/SUBMIT_STEP_CONFIG_SUCCESS",
  SUBMIT_STEP_CONFIG_FAILURE = "@process/SUBMIT_STEP_CONFIG_FAILURE",

  SUBMIT_FEEDBACK_REQUEST = "@process/SUBMIT_FEEDBACK_REQUEST",
  SUBMIT_FEEDBACK_SUCCESS = "@process/SUBMIT_FEEDBACK_SUCCESS",
  SUBMIT_FEEDBACK_FAILURE = "@process/SUBMIT_FEEDBACK_FAILURE",

  DELETE_STEP_REQUEST = "@process/DELETE_STEP_REQUEST",
  DELETE_STEP_SUCCESS = "@process/DELETE_STEP_SUCCESS",
  DELETE_STEP_FAILURE = "@process/DELETE_STEP_FAILURE",

  // Optimistic Update Actions
  OPTIMISTIC_UPDATE_FEEDBACK = "@process/OPTIMISTIC_UPDATE_FEEDBACK",
  OPTIMISTIC_UPDATE_STEP_CONFIG = "@process/OPTIMISTIC_UPDATE_STEP_CONFIG",
  OPTIMISTIC_DELETE_STEP = "@process/OPTIMISTIC_DELETE_STEP",

  // UI Actions
  SET_SELECTED_OFFICE_TYPE = "@process/SET_SELECTED_OFFICE_TYPE",
  OPEN_STEP_DETAIL_MODAL = "@process/OPEN_STEP_DETAIL_MODAL",
  CLOSE_STEP_DETAIL_MODAL = "@process/CLOSE_STEP_DETAIL_MODAL",
  OPEN_FEEDBACK_MODAL = "@process/OPEN_FEEDBACK_MODAL",
  CLOSE_FEEDBACK_MODAL = "@process/CLOSE_FEEDBACK_MODAL",
  OPEN_CANVAS_VIEW_MODAL = "@process/OPEN_CANVAS_VIEW_MODAL",
  CLOSE_CANVAS_VIEW_MODAL = "@process/CLOSE_CANVAS_VIEW_MODAL",
}

// State Types
export interface WorkflowState {
  categories: WorkflowCategoryData[];
  loading: boolean;
  error: string | null;
}

export interface ProcessUIState {
  selectedOfficeType: OfficeType;
  selectedStep: WorkflowStepData | null;
  isStepDetailModalOpen: boolean;
  feedbackTarget: {
    type: "category" | "step";
    id: string;
    name: string;
    feedbackHistory?: FeedbackEntry[];
  } | null;
  isFeedbackModalOpen: boolean;
  isCanvasViewOpen: boolean;
  canvasViewCategory: WorkflowCategoryData | null;
}

export interface ProcessRootState {
  workflows: WorkflowState;
  processUi: ProcessUIState;
}

// Action Interfaces
export interface FetchWorkflowsRequestAction {
  type: ProcessActionTypes.FETCH_WORKFLOWS_REQUEST;
}

export interface FetchWorkflowsSuccessAction {
  type: ProcessActionTypes.FETCH_WORKFLOWS_SUCCESS;
  payload: WorkflowCategoryData[];
}

export interface FetchWorkflowsFailureAction {
  type: ProcessActionTypes.FETCH_WORKFLOWS_FAILURE;
  payload: string;
}

export interface SubmitStepConfigRequestAction {
  type: ProcessActionTypes.SUBMIT_STEP_CONFIG_REQUEST;
  payload: SubmitStepPayload;
}

export interface SubmitStepConfigSuccessAction {
  type: ProcessActionTypes.SUBMIT_STEP_CONFIG_SUCCESS;
  payload: WorkflowCategoryData[];
}

export interface SubmitStepConfigFailureAction {
  type: ProcessActionTypes.SUBMIT_STEP_CONFIG_FAILURE;
  payload: string;
}

export interface SubmitFeedbackRequestAction {
  type: ProcessActionTypes.SUBMIT_FEEDBACK_REQUEST;
  payload: SubmitFeedbackPayload;
}

export interface SubmitFeedbackSuccessAction {
  type: ProcessActionTypes.SUBMIT_FEEDBACK_SUCCESS;
  payload: WorkflowCategoryData[];
}

export interface SubmitFeedbackFailureAction {
  type: ProcessActionTypes.SUBMIT_FEEDBACK_FAILURE;
  payload: string;
}

export interface DeleteStepRequestAction {
  type: ProcessActionTypes.DELETE_STEP_REQUEST;
  payload: DeleteStepPayload;
}

export interface DeleteStepSuccessAction {
  type: ProcessActionTypes.DELETE_STEP_SUCCESS;
  payload: WorkflowCategoryData[];
}

export interface DeleteStepFailureAction {
  type: ProcessActionTypes.DELETE_STEP_FAILURE;
  payload: string;
}

export interface SetSelectedOfficeTypeAction {
  type: ProcessActionTypes.SET_SELECTED_OFFICE_TYPE;
  payload: OfficeType;
}

export interface OpenStepDetailModalAction {
  type: ProcessActionTypes.OPEN_STEP_DETAIL_MODAL;
  payload: WorkflowStepData;
}

export interface CloseStepDetailModalAction {
  type: ProcessActionTypes.CLOSE_STEP_DETAIL_MODAL;
}

export interface OpenFeedbackModalAction {
  type: ProcessActionTypes.OPEN_FEEDBACK_MODAL;
  payload: {
    type: "category" | "step";
    id: string;
    name: string;
    feedbackHistory?: FeedbackEntry[];
  };
}

export interface CloseFeedbackModalAction {
  type: ProcessActionTypes.CLOSE_FEEDBACK_MODAL;
}

export interface OpenCanvasViewModalAction {
  type: ProcessActionTypes.OPEN_CANVAS_VIEW_MODAL;
  payload: WorkflowCategoryData;
}

export interface CloseCanvasViewModalAction {
  type: ProcessActionTypes.CLOSE_CANVAS_VIEW_MODAL;
}

export interface OptimisticUpdateFeedbackAction {
  type: ProcessActionTypes.OPTIMISTIC_UPDATE_FEEDBACK;
  payload: {
    targetType: "category" | "step";
    targetId: string;
    feedbackEntry: FeedbackEntry;
  };
}

export interface OptimisticUpdateStepConfigAction {
  type: ProcessActionTypes.OPTIMISTIC_UPDATE_STEP_CONFIG;
  payload: SubmitStepPayload;
}

export interface OptimisticDeleteStepAction {
  type: ProcessActionTypes.OPTIMISTIC_DELETE_STEP;
  payload: DeleteStepPayload;
}

export type ProcessActionType =
  | FetchWorkflowsRequestAction
  | FetchWorkflowsSuccessAction
  | FetchWorkflowsFailureAction
  | SubmitStepConfigRequestAction
  | SubmitStepConfigSuccessAction
  | SubmitStepConfigFailureAction
  | SubmitFeedbackRequestAction
  | SubmitFeedbackSuccessAction
  | SubmitFeedbackFailureAction
  | DeleteStepRequestAction
  | DeleteStepSuccessAction
  | DeleteStepFailureAction
  | OptimisticUpdateFeedbackAction
  | OptimisticUpdateStepConfigAction
  | OptimisticDeleteStepAction
  | SetSelectedOfficeTypeAction
  | OpenStepDetailModalAction
  | CloseStepDetailModalAction
  | OpenFeedbackModalAction
  | CloseFeedbackModalAction
  | OpenCanvasViewModalAction
  | CloseCanvasViewModalAction;
